import type { Band, Book, BookState } from "../../api/pqx";

/** States in which the engine is still working on a book. */
export const RUNNING_STATES = new Set<BookState>([
  "gate_failed",
  "queued",
  "converting",
  "extracting",
  "checking_answers",
  "done",
  "importing",
]);

const BOOK_LABELS: Record<BookState, string> = {
  queued: "Queued",
  converting: "Converting",
  extracting: "Extracting questions",
  checking_answers: "Checking answers",
  done: "Importing",
  importing: "Importing",
  imported: "Ready for review",
  failed: "Failed",
  gate_failed: "Importing for review",
  import_failed: "Import failed",
  cancelled: "Cancelled",
};

export const bookStateLabel = (state: BookState) => BOOK_LABELS[state] ?? state;

/**
 * The checking pass the engine is in after extraction — "checking answers",
 * "rereading", "settling disputes" and so on — with how far through it. Each
 * pass counts from 0 on its own. Null outside one, and from engines that don't
 * report passes, which only say "Checking answers".
 */
export function checkPass(
  progress: Book["progress"],
): { step: string; done: number; total: number; percent: number } | null {
  if (!progress?.checks_total) return null;
  const named = /^(.+?) \d+\/\d+$/.exec(progress.detail.trim())?.[1];
  const step = named ?? "checking answers";
  return {
    step: step.charAt(0).toUpperCase() + step.slice(1),
    done: progress.checks_done ?? 0,
    total: progress.checks_total,
    percent: progress.check_percent ?? 0,
  };
}

export const BOOK_STATE_CLASSES: Partial<Record<BookState, string>> = {
  imported: "bg-positive/15 text-positive",
  failed: "bg-negative/15 text-negative",
  import_failed: "bg-negative/15 text-negative",
  gate_failed: "bg-surface-high text-text",
  cancelled: "bg-surface-high text-text-muted",
};

export const BAND_CLASSES: Record<Band, string> = {
  red: "bg-negative/15 text-negative",
  amber: "bg-warning/15 text-warning",
  green: "bg-positive/15 text-positive",
};

export const BAND_LABELS: Record<Band, string> = {
  red: "Needs fixing",
  amber: "Check",
  green: "Looks right",
};

/** "Queued · 1st in line", or just the state's label when it has no place. */
export function ordinal(n: number): string {
  const tens = n % 100;
  const suffix = tens >= 11 && tens <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" }[n % 10] ?? "th");
  return `${n}${suffix}`;
}

export function queueLabel(position: number | null): string | null {
  return position ? `${ordinal(position)} in line` : null;
}

/**
 * Where a question stands on images, for the review list.
 *
 * - `missing`: it needs one and has none — the engine saw a figure it could not
 *   crop, or an open issue says a figure is missing. The ones to fix.
 * - `extra`: it has one, but the engine found more than the one kept.
 * - `has`: it has one, nothing more expected.
 * - `none`: no image involved.
 */
export type ImageStatus = "missing" | "extra" | "has" | "none";

export function imageStatus(q: {
  image_url: string | null;
  engine_figures: { cropped: number; uncropped: boolean } | null;
  issues?: { check: string; field: string | null; dismissed: boolean }[];
}): ImageStatus {
  const flagged = (q.issues ?? []).some(
    (i) =>
      !i.dismissed && (i.check === "G3" || i.field?.startsWith("figures") || i.field === "image"),
  );
  if (!q.image_url) return q.engine_figures?.uncropped || flagged ? "missing" : "none";
  return (q.engine_figures?.cropped ?? 0) > 1 ? "extra" : "has";
}

export const IMAGE_BADGES: Record<
  Exclude<ImageStatus, "none">,
  { label: string; className: string }
> = {
  missing: { label: "Image needed", className: "bg-negative/15 text-negative" },
  extra: { label: "Extra images", className: "bg-warning/15 text-warning" },
  has: { label: "Has image", className: "bg-info/15 text-info" },
};
